import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, FileCheck2, AlertTriangle, PhoneCall, Lock, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

const PILLARS = [
  { icon: FileCheck2, title: "Every worker is verified", text: "NDIS Worker Screening Clearance, National Police Check, First Aid, photo ID, and Public Liability — checked by our team before a worker can take a shift." },
  { icon: Shield, title: "NDIS Quality & Safeguards aligned", text: "We follow the NDIS Quality and Safeguarding Framework, including the Code of Conduct, Practice Standards, and incident reporting requirements." },
  { icon: AlertTriangle, title: "Incident reporting in-app", text: "Participants and workers can lodge an incident in two taps. Serious incidents are escalated to BTR Health admin and the NDIS Commission where required." },
  { icon: PhoneCall, title: "Real Australian support", text: "Our support team is based in Sydney and answers the phone Mon–Sat. Emergency? We escalate within 30 minutes." },
  { icon: Lock, title: "Your data, encrypted", text: "TLS in transit, encrypted at rest, hosted in Australia. We never sell your data and only share what's needed for invoicing." }
];

export default function SafetyPage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="container mx-auto px-4 py-5 flex items-center justify-between max-w-6xl">
        <Link to="/" className="font-bold text-xl text-slate-900">BTR Health</Link>
        <div className="flex gap-3">
          <Link to="/"><Button variant="outline" className="rounded-full">Home</Button></Link>
          <Link to="/register"><Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full">Get started</Button></Link>
        </div>
      </header>

      <section className="container mx-auto px-4 py-14 max-w-4xl text-center">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-6xl font-bold text-slate-900 mb-5 leading-tight">
          Your safety is non-negotiable.
        </motion.h1>
        <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
          We follow the NDIS Quality and Safeguarding Framework end-to-end. Here is exactly how we keep participants and workers safe.
        </p>
      </section>

      <section className="container mx-auto px-4 max-w-5xl mb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PILLARS.map(p => (
            <div key={p.title} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
              <div className="w-11 h-11 rounded-xl bg-[#87CEEB]/10 flex items-center justify-center mb-3">
                <p.icon className="w-5 h-5 text-[#87CEEB]" />
              </div>
              <h4 className="font-bold text-slate-900 mb-2 text-lg">{p.title}</h4>
              <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">Need to report something?</h2>
          <div className="bg-white rounded-2xl border border-slate-100 p-6">
            <p className="text-slate-700 mb-4">If you have a safety concern about a shift, worker, or participant, please report it as soon as possible. All reports are reviewed by a real person.</p>
            <div className="space-y-3 text-sm text-slate-700">
              <p>📧 <strong>Email:</strong> <a className="text-[#87CEEB] hover:underline" href="mailto:safety@btrhealth.com.au">safety@btrhealth.com.au</a></p>
              <p>📞 <strong>Phone (Mon–Sat 8am–8pm AEST):</strong> 1800 BTR HLTH</p>
              <p>🚨 <strong>Life-threatening emergency:</strong> Call 000 first, then notify us.</p>
            </div>
            <div className="mt-5">
              <Link to="/report-incident">
                <Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full" data-testid="safety-report-cta">
                  Lodge an incident report <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-14 max-w-3xl">
        <div className="bg-slate-900 text-white rounded-3xl p-8 md:p-12 text-center">
          <h3 className="text-2xl md:text-3xl font-bold mb-3">Operating under NDIS standards</h3>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">BTR Health operates in line with the NDIS Code of Conduct and the NDIS Practice Standards. All our subcontracted support workers are individually screened and verified.</p>
          <p className="text-xs text-slate-500">ABN: TBD · Compliance: ndiscommission.gov.au</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
